/**
 * Matching for the header's page-jump search.
 *
 * Lives here rather than inline in Header.vue so it can be tested directly -- the alias fallback
 * below is the console's answer to a rename, and "typing the old name finds the new page" is a
 * promise worth holding with a test rather than with care.
 */

export interface SearchableRouteLike {
  name: unknown
  /**
   * The page's unambiguous name, from the route's `navLabel` meta -- "Time Card Editor" for the tab
   * whose trail ends in just "Editor". A current name, so a match on it is not a former-name match.
   */
  navLabel?: string
  /** Former names for this page, from the route's `searchAliases` meta. */
  searchAliases?: string[]
  /** The human-readable trail shown in the results list, e.g. "Reports Center / My Saved Views". */
  getRoutePathString: () => string
}

export type SearchMatch<T> = T & {
  /**
   * Set when the match came from a former name rather than the current one, so the result can
   * show which. Absent on an ordinary match.
   */
  matchedAlias?: string
}

/**
 * Routes matching every term in `query`.
 *
 * Terms are matched across everything known about a route at once -- the trail people see, the
 * internal route name, and any former names -- rather than requiring one field to satisfy the
 * whole query. Half-remembered searches are the normal case during a rename: "memorized views"
 * is one word from the old vocabulary and one from the new, and matching field by field found
 * nothing for it.
 *
 * Results that match on the visible trail come first, so a former name can never bury a page
 * that still genuinely carries the typed word.
 */
export function matchSearchableRoutes<T extends SearchableRouteLike> (
  routes: T[],
  query: string
): SearchMatch<T>[] {
  const queryTerms = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (queryTerms.length === 0) {
    return []
  }

  const byCurrentName: SearchMatch<T>[] = []
  const byFormerName: SearchMatch<T>[] = []

  for (const route of routes) {
    const currentNames = [route.getRoutePathString(), route.navLabel ?? ''].join(' ').toLowerCase()
    const name = typeof route.name === 'string' ? route.name.toLowerCase() : ''
    const aliases = route.searchAliases ?? []

    const haystack = [currentNames, name, ...aliases.map(alias => alias.toLowerCase())].join(' ')
    if (!queryTerms.every(term => haystack.includes(term))) {
      continue
    }

    // Which terms the name on screen does not account for. Anything left over is what the
    // person remembered from before, and is what the result should explain.
    const unexplained = queryTerms.filter(term => !currentNames.includes(term))

    if (unexplained.length === 0) {
      byCurrentName.push(route)
      continue
    }

    // Deliberately not keyed off which field produced the match. The internal route name here is
    // itself a fossil -- 'memorized' for the page now called My Saved Views -- so matching on it
    // looks like a hit on the current name and used to swallow the explanation, leaving someone
    // who typed "memorized" with an unexplained result for a page that says My Saved Views.
    const matchedAlias = aliases.find(alias => {
      const lowered = alias.toLowerCase()
      return unexplained.every(term => lowered.includes(term))
    })

    if (matchedAlias) {
      byFormerName.push({ ...route, matchedAlias })
    } else {
      byCurrentName.push(route)
    }
  }

  return [...byCurrentName, ...byFormerName]
}
